import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Modal, Text, Button, Loader, Icon } from "@ui-component-library";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { ContentContainer } from "./utils/utils";

import SelectPropertyDialog from "../../SubscriptionCheckout/components/SelectPropertyDialog";
import { mapPropertiesData } from "../../SubscriptionCheckout/utils";
import { usePropsDataStore } from "@store/PropsDataStore";

const applySboDfr = (selected, allProps) => {
  if (selected.type !== "SBO") return selected;
  const property = allProps.find(
    (prop) => prop.type === "PROP" && prop.designation === selected.designation
  );
  return { ...selected, dfr: property?.dfr ?? selected.dfr };
};

export const PropertySelectStep = ({
  handleNext,
  createCo2Deal,
}) => {
  const { props } = usePropsDataStore();
  const [selectedProp, setSelectedProp] = useState(null);
  const [propSearchError, setPropSearchError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);

  const mergedProps = useMemo(() => {
    const list = [...(Window.react_data?.owned_props ?? [])];
    props.forEach((storeProp) => {
      const alreadyListed = list.some(
        (existingProp) => existingProp.ref == storeProp.ref
      );
      if (!alreadyListed) {
        list.push(storeProp);
      }
    });
    return list;
  }, [props]);

  const userProperties = useMemo(
    () => mapPropertiesData(mergedProps),
    [mergedProps]
  );

  const selectedPropertiesForDialog = useMemo(
    () =>
      selectedProp
        ? [
          {
            ref: String(selectedProp.ref),
            designation: String(selectedProp.designation ?? ""),
          },
        ]
        : [],
    [selectedProp]
  );

  const handleSubmit = async () => {
    const isShared = selectedProp.role
      ? selectedProp.role !== "owner"
      : Boolean(selectedProp.is_shared);
    if (isShared) {
      setPropSearchError(
        "Endast fastighetens ägare kan ansöka om kolkrediter. Den här fastigheten är delad med dig."
      );
      return;
    }

    const sbo = mergedProps.find(
      (item) =>
        item.type === "SBO" && item.designation === selectedProp.designation
    );
    setIsLoading(true);
    const result = await createCo2Deal(selectedProp.designation, selectedProp.ref);
    if (!result || result.id === undefined) {
      setIsLoading(false);
      return;
    }
    handleNext({
      data: {
        selectedProp,
        sbo,
      },
    });
    setIsLoading(false);
  };

  const handlePropertySelect = (option) => {
    const ref = String(option.value);
    const propertyFromStore = mergedProps.find((p) => String(p.ref) === ref);
    if (!propertyFromStore) return;
    setSelectedProp((prev) => {
      if (prev && String(prev.ref) === ref) {
        return null;
      }
      return applySboDfr({ ...propertyFromStore }, mergedProps);
    });
    setPropSearchError("");
  };

  const handleRemove = () => {
    setSelectedProp(null);
    setPropSearchError("");
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <Text variant="text-base/leading-normal/normal">
          Hämtar din fastighetsdata...
        </Text>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Modal.Content>
        <ContentContainer>
          <Text variant="text-xl/leading-normal/medium">
            Har du potential för kolkrediter i din skog?
          </Text>
          <Text variant="text-sm/leading-normal/normal">
            Din skog kan spela en viktig roll i kampen mot klimatförändringarna.
            Genom att låta träden lagra koldioxid skapar du inte bara en
            hållbar framtid, utan kan även få nya intäktstmöjligheter.
          </Text>
          <div className="mt-4 flex flex-col gap-2">
            <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
              Välj den fastighet du vill ansöka för
            </Text>
            <div className="w-full rounded-2xl bg-[#FAF9F8] p-4">
              <div className="flex flex-row items-center justify-between gap-2">
                <Text variant="text-sm/leading-normal/medium">
                  {!selectedProp ? "Inga fastigheter valda" : "Fastighet"}
                </Text>
                {!selectedProp ? (
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => setPropertyDialogOpen(true)}
                  >
                    Lägg till
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setPropertyDialogOpen(true)}
                  >
                    Ändra
                  </Button>
                )}
              </div>
              {selectedProp ? (
                <div className="mt-4 flex flex-col gap-4 border-t border-solid border-[#E6E6E6] pt-4">
                  <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-col gap-1">
                      <Text variant="text-sm/leading-normal/medium" className="capitalize">
                        {selectedProp.designation?.toLowerCase()}
                      </Text>
                    </div>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={handleRemove}
                    >
                      Radera
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
            {propSearchError ? (
              <Text variant="text-xs/leading-normal/normal" className="text-danger-500">
                {propSearchError}
              </Text>
            ) : null}
          </div>
        </ContentContainer>
      </Modal.Content>
      <Modal.Footer>
        <Button
          fullWidth
          variant="primary"
          onClick={handleSubmit}
          disabled={!selectedProp}
        >
          Nästa steg
          <Icon name="ArrowRightSimple" />
        </Button>
      </Modal.Footer>

      {propertyDialogOpen &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1070,
            }}
          >
            <TailwindWrapper>
              <SelectPropertyDialog
                isOpen
                onClose={() => setPropertyDialogOpen(false)}
                userProperties={userProperties}
                selectedProperties={selectedPropertiesForDialog}
                onSelect={handlePropertySelect}
                startedAsGuest={false}
                allowNonPropOwnedSelection
                maxPendingSelections={1}
                hideSearchField
              />
            </TailwindWrapper>
          </div>,
          document.body
        )}
    </>
  );
};
